import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tab,
  Tabs,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import dayjs from "../../shared utils/dayjsConfig.js";
import AddBedroomDialog from "./AddBedroomDialog";
import BedroomBoardView from "./BedroomBoardView";
import CapacityConflictDialog from "./CapacityConflictDialog";
import BedroomFilters from "./BedroomFilters";
import BedroomTableView from "./BedroomTableView";
import RoomManagementView from "./RoomManagementView";
import useBedroomAllocation from "../hooks/useBedroomAllocation";
import "../Bedrooms.css";

function BedroomsModule({
  selectedYear,
  onSelectStudent,
  onSelectAdult,
  selectedDate: externalDate,
  selectedCampusId: externalCampus,
  setSelectedDate: setExternalDate,
  setSelectedCampusId: setExternalCampus,
  navGuardRef,
}) {
  const [view, setView] = useState("board");
  
  const {
    selectedDate,
    selectedCampus,
    selectedHouse,
    selectedGender,
    selectedAge,
    selectedNationality,
    nameSearch,
    roomNameSearch,
    roomFilterMode,
    sortField,
    sortAsc,
    loading,
    isSaving,
    toast,
    addRoomDialogOpen,
    visibleTableResidents,
    visibleBoardRooms,
    displayState,
    managementRooms,
    filteredResidents,
    campusOptions,
    houses,
    genders,
    ages,
    nationalities,
    availableRoomNames,
    validationErrors,
    placementWarnings,
    coverageGaps,
    dismissResidentWarning,
    undoResidentWarning,
    clearAllWarnings,
    wholeStayPrompt,
    confirmWholeStay,
    keepFromViewedDate,
    cancelWholeStayMove,
    capacityConflict,
    confirmCapacityClamp,
    revertCapacityConflict,
    dismissCapacityConflict,
    handleSaveRoomSchedule,
    closeToast,
    setSelectedCampus,
    setSelectedHouse,
    setSelectedGender,
    setSelectedAge,
    setSelectedNationality,
    setNameSearch,
    setRoomNameSearch,
    setRoomFilterMode,
    setAddRoomDialogOpen,
    goToDate,
    hasUnsavedChanges,
    moveResident,
    assignResidentByRoomName,
    clearRoomAssignment,
    saveChanges,
    downloadFireList,
    downloadRoomPdf,
    downloadRoomSigns,
    handleAddRoom,
    handleUpdateRoom,
    handleBulkUpdateRooms,
    handleDeleteRoom,
    getAssignedRoomName,
    getAssignedRoomHouse,
    getAssignedRoomEnsuite,
    getDraftRoomName,
    handleSort,
  } = useBedroomAllocation(selectedYear);

  const [managementSearch, setManagementSearch] = useState("");
  const [managementSelectedCampus, setManagementSelectedCampus] = useState("");
  const [managementSelectedHouse, setManagementSelectedHouse] = useState("");
  const lastAppliedDateRef = useRef(null);
  const lastAppliedCampusRef = useRef(null);
  const [pendingDate, setPendingDate] = useState(null);

  // Expose unsaved-changes state + save action to the parent so it can guard
  // navigation away from the allocator (top menu, home, logout).
  useEffect(() => {
    if (!navGuardRef) return undefined;
    navGuardRef.current = { dirty: hasUnsavedChanges, save: saveChanges };
    return () => {
      navGuardRef.current = null;
    };
  }, [navGuardRef, hasUnsavedChanges, saveChanges]);

  // Warn on browser-level navigation (refresh, tab close, back) while dirty.
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!externalDate) return;

    const d = dayjs(externalDate);
    if (!d.isValid()) return;

    const dateKey = d.format("YYYY-MM-DD");
    if (lastAppliedDateRef.current === dateKey) return;

    goToDate(d);
    lastAppliedDateRef.current = dateKey;
  }, [externalDate, goToDate]);

  useEffect(() => {
    if (!externalCampus) return;
    if (lastAppliedCampusRef.current === String(externalCampus)) return;

    setSelectedCampus(String(externalCampus));
    lastAppliedCampusRef.current = String(externalCampus);
  }, [externalCampus, setSelectedCampus]);

  const applyDateChange = (target) => {
    goToDate(target);
    setExternalDate?.(target);
  };

  // Guard navigation: with unsaved changes, ask Save / Discard / Cancel instead
  // of silently saving (or losing) the user's work.
  const requestDateChange = (value) => {
    const nextDate = dayjs(value);
    if (!nextDate.isValid()) return;

    if (hasUnsavedChanges) {
      setPendingDate(nextDate);
    } else {
      applyDateChange(nextDate);
    }
  };

  const handleDateChange = (value) => requestDateChange(value);

  const handleShiftDate = (increment) =>
    requestDateChange(dayjs(selectedDate).add(increment, "day"));

  const handlePendingSave = async () => {
    const target = pendingDate;
    try {
      const res = await saveChanges();
      // Over-capacity (resolve in the capacity dialog) or stale board (reloaded,
      // edits dropped): close this prompt but stay on the date — don't navigate.
      if (res?.conflict) {
        setPendingDate(null);
        return;
      }
      setPendingDate(null);
      applyDateChange(target);
    } catch (e) {
      // Save/validation failed (toast already shown) — keep the dialog open.
    }
  };

  const handlePendingDiscard = () => {
    const target = pendingDate;
    setPendingDate(null);
    applyDateChange(target);
  };

  const handleCampusChange = (value) => {
    setSelectedCampus(value);
    setExternalCampus?.(value || "");
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", p: 2 }}>
      <Tabs value={view} onChange={(_, nextValue) => setView(nextValue)} sx={{ mb: 2 }}>
        <Tab value="board" label="Board Allocator" />
        <Tab value="table" label="Table Allocator" />
        <Tab value="rooms" label="Room Management" />
      </Tabs>

      {view !== "rooms" && (
        <BedroomFilters
          mode={view}
          selectedDate={selectedDate}
          campuses={campusOptions}
          houses={houses}
          genders={genders}
          ages={ages}
          nationalities={nationalities}
          selectedCampus={selectedCampus}
          selectedHouse={selectedHouse}
          selectedGender={selectedGender}
          selectedAge={selectedAge}
          selectedNationality={selectedNationality}
          nameSearch={nameSearch}
          roomNameSearch={roomNameSearch}
          roomFilterMode={roomFilterMode}
          onDateChange={handleDateChange}
          onShiftDate={handleShiftDate}
          onCampusChange={handleCampusChange}
          onHouseChange={setSelectedHouse}
          onGenderChange={setSelectedGender}
          onAgeChange={setSelectedAge}
          onNationalityChange={setSelectedNationality}
          onNameSearchChange={setNameSearch}
          onRoomSearchChange={setRoomNameSearch}
          onRoomFilterModeChange={setRoomFilterMode}
        />
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : view === "board" ? (
        <BedroomBoardView
          rooms={visibleBoardRooms}
          allRooms={displayState.rooms}
          filteredResidents={filteredResidents}
          noCampusSelected={!selectedCampus}
          nameSearch={nameSearch}
          onMoveResident={moveResident}
          onSave={saveChanges}
          isSaving={isSaving}
          onDownloadFireList={downloadFireList}
          onDownloadRoomPdf={downloadRoomPdf}
          onDownloadRoomSigns={downloadRoomSigns}
          warnings={placementWarnings}
          onUndoWarning={undoResidentWarning}
          onDismissWarning={dismissResidentWarning}
          onClearWarnings={clearAllWarnings}
          coverageGaps={coverageGaps}
          wholeStayPrompt={wholeStayPrompt}
          onWholeStay={confirmWholeStay}
          onKeepFromDate={keepFromViewedDate}
          onCancelWholeStay={cancelWholeStayMove}
        />
      ) : view === "table" ? (
        <BedroomTableView
          residents={visibleTableResidents}
          noCampusSelected={!selectedCampus}
          availableRoomNames={availableRoomNames}
          validationErrors={validationErrors}
          getAssignedRoomName={getAssignedRoomName}
          getAssignedRoomHouse={getAssignedRoomHouse}
          getAssignedRoomEnsuite={getAssignedRoomEnsuite}
          getDraftRoomName={getDraftRoomName}
          onAssignByRoomName={assignResidentByRoomName}
          onClearRoom={clearRoomAssignment}
          onSort={handleSort}
          sortField={sortField}
          sortAsc={sortAsc}
          onSave={saveChanges}
          isSaving={isSaving}
          onDownloadFireList={downloadFireList}
          onDownloadRoomPdf={downloadRoomPdf}
          onDownloadRoomSigns={downloadRoomSigns}
          onCreateRoom={() => setAddRoomDialogOpen(true)}
          warnings={placementWarnings}
          onUndoWarning={undoResidentWarning}
          onDismissWarning={dismissResidentWarning}
          onClearWarnings={clearAllWarnings}
          coverageGaps={coverageGaps}
        />
      ) : (
        <>
          <RoomManagementView
            rooms={managementRooms}
            selectedYear={selectedYear}
            search={managementSearch}
            selectedCampus={managementSelectedCampus}
            selectedHouse={managementSelectedHouse}
            onSearchChange={setManagementSearch}
            onSelectedCampusChange={setManagementSelectedCampus}
            onSelectedHouseChange={setManagementSelectedHouse}
            onCreateRoom={handleAddRoom}
            onEditRoom={handleUpdateRoom}
            onBulkEditRooms={handleBulkUpdateRooms}
            onDeleteRoom={handleDeleteRoom}
            onSaveRoomSchedule={handleSaveRoomSchedule}
          />
          <AddBedroomDialog
            open={addRoomDialogOpen}
            onClose={() => setAddRoomDialogOpen(false)}
            selectedCampus=""
            campuses={campusOptions}
            onSave={async (bedroom) => {
              await handleAddRoom(bedroom);
              setAddRoomDialogOpen(false);
            }}
          />
        </>
      )}

      <CapacityConflictDialog
        conflict={capacityConflict}
        onClamp={confirmCapacityClamp}
        onRevert={revertCapacityConflict}
        onDismiss={dismissCapacityConflict}
      />

      <Dialog open={Boolean(pendingDate)} onClose={() => setPendingDate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unsaved room changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved room assignment changes for{" "}
            {selectedDate ? dayjs(selectedDate).format("DD MMM YYYY") : "this date"}. Save
            them before moving to{" "}
            {pendingDate ? dayjs(pendingDate).format("DD MMM YYYY") : "the new date"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDate(null)}>Cancel</Button>
          <Button color="error" onClick={handlePendingDiscard}>
            Discard &amp; continue
          </Button>
          <Button variant="contained" onClick={handlePendingSave}>
            Save &amp; continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration ?? 4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BedroomsModule;